/**
 * device/apps/journal.ts — The Journal / Quest Log
 *
 * A warm, paper-textured quest journal and whisper collection for the
 * in-game device. Two tabs: "Quests" shows active/completed/locked
 * quest entries with progress bars and status indicators. "Whispers"
 * collects memorable things the Aeterna has said — contemplative,
 * poetic fragments displayed as quiet cards with quotation watermarks.
 *
 * Everything is Canvas 2D. No DOM. The scroll offset and active tab
 * persist in appStates["journal"].
 */

import type { GameState, QuestProgress, CollectedWhisper } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText, drawProgressBar } from "../app.js";
import { QUEST_INDEX } from "../../narrative/quests.js";

// ─── Constants ───────────────────────────────────────────────────────────────

const APP_ID = "journal";
const APP_NAME = "Journal";

// Colors — warm paper-like palette
const C_BG              = "#0e0c0a";
const C_TAB_BG          = "rgba(20,16,12,0.9)";
const C_TAB_ACTIVE      = "#c8a878";
const C_TAB_INACTIVE    = "rgba(200,168,120,0.35)";
const C_TAB_INDICATOR   = "#c8a878";
const C_CARD_BG         = "rgba(28,24,18,0.85)";
const C_CARD_BORDER     = "rgba(200,168,120,0.08)";
const C_ACCENT_ACTIVE   = "#8b5cf6";
const C_ACCENT_COMPLETE = "#4ade80";
const C_ACCENT_LOCKED   = "#555550";
const C_TEXT_TITLE       = "#f5e6d0";
const C_TEXT_DESC        = "rgba(245,230,208,0.55)";
const C_TEXT_DIM         = "rgba(245,230,208,0.3)";
const C_PROGRESS_BG     = "rgba(200,168,120,0.1)";
const C_PROGRESS_FG     = "#8b5cf6";
const C_COMPLETE_CHECK  = "#4ade80";
const C_LOCK_ICON       = "#555550";
const C_WHISPER_TEXT    = "rgba(245,230,208,0.75)";
const C_WHISPER_TIME    = "rgba(245,230,208,0.25)";
const C_WHISPER_QUOTE   = "rgba(200,168,120,0.06)";

// Layout
const TAB_BAR_H       = 28;
const CARD_PAD_X      = 8;
const CARD_GAP        = 6;
const CARD_RADIUS     = 6;
const CARD_INNER_PAD  = 8;
const ACCENT_BAR_W    = 3;
const PROGRESS_BAR_H  = 4;
const QUEST_CARD_MIN_H = 58;
const WHISPER_CARD_MIN_H = 44;
const SCROLL_SPEED    = 30;
const FONT_TITLE      = "bold 10px sans-serif";
const FONT_DESC       = "9px sans-serif";
const FONT_SMALL      = "8px sans-serif";
const FONT_TAB        = "bold 9px sans-serif";
const FONT_WHISPER    = "italic 9px sans-serif";
const FONT_QUOTE_MARK = "bold 28px Georgia, serif";
const LINE_HEIGHT     = 12;

// ─── Data Types ──────────────────────────────────────────────────────────────

/** Simplified quest view for rendering — derived from PlayerState + quest registry */
interface QuestEntry {
  title: string;
  description: string;
  progress: number;   // 0–1
  status: "active" | "completed" | "locked";
}

/** Simplified whisper view for rendering */
interface WhisperEntry {
  text: string;
  timestamp: string;  // display string like "Dawn, Day 3"
}

// ─── Data Accessors ─────────────────────────────────────────────────────────

/** Derive renderable quest entries from player state + quest registry */
function getQuestEntries(state: GameState): QuestEntry[] {
  const quests = state.player.quests;
  const entries: QuestEntry[] = [];

  for (const qp of quests) {
    const questDef = QUEST_INDEX.get(qp.questId);
    // Map QuestStatus to the simplified status the journal renders
    let status: "active" | "completed" | "locked";
    if (qp.status === "complete") status = "completed";
    else if (qp.status === "active" || qp.status === "available") status = "active";
    else status = "locked"; // locked or abandoned

    entries.push({
      title: questDef?.name ?? qp.questId,
      description: questDef?.description ?? "A quest whose details have faded from memory.",
      progress: qp.progress,
      status,
    });
  }

  return entries;
}

/** Derive renderable whisper entries from player's collected whispers */
function getWhisperEntries(state: GameState): WhisperEntry[] {
  return state.player.collectedWhispers.map(w => ({
    text: w.text,
    timestamp: w.displayTime,
  }));
}

// ─── Per-Instance App State ──────────────────────────────────────────────────

type TabId = "quests" | "whispers";

interface JournalAppState {
  activeTab: TabId;
  questScrollOffset: number;
  whisperScrollOffset: number;
  questScrollVelocity: number;
  whisperScrollVelocity: number;
  questMaxScroll: number;
  whisperMaxScroll: number;
  elapsed: number;
}

function getAppState(state: GameState): JournalAppState {
  if (!state.player.device.appStates[APP_ID]) {
    state.player.device.appStates[APP_ID] = {
      activeTab: "quests" as TabId,
      questScrollOffset: 0,
      whisperScrollOffset: 0,
      questScrollVelocity: 0,
      whisperScrollVelocity: 0,
      questMaxScroll: 0,
      whisperMaxScroll: 0,
      elapsed: 0,
    };
  }
  return state.player.device.appStates[APP_ID] as JournalAppState;
}

// ─── Text Wrapping ───────────────────────────────────────────────────────────

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  font: string,
): string[] {
  ctx.font = font;
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.length > 0 ? lines : [""];
}

// ─── Quest Card Renderer ─────────────────────────────────────────────────────

function measureQuestCard(
  ctx: CanvasRenderingContext2D,
  quest: QuestEntry,
  cardW: number,
): { descLines: string[]; height: number } {
  const textW = cardW - CARD_INNER_PAD * 2 - ACCENT_BAR_W - 4;
  const descLines = wrapText(ctx, quest.description, textW, FONT_DESC);
  // title line + desc lines + gap + progress bar + padding
  const titleH = LINE_HEIGHT + 2;
  const descH = descLines.length * LINE_HEIGHT;
  const progressH = quest.status !== "locked" ? PROGRESS_BAR_H + 6 : 0;
  const height = Math.max(
    QUEST_CARD_MIN_H,
    CARD_INNER_PAD + titleH + descH + progressH + CARD_INNER_PAD,
  );
  return { descLines, height };
}

function drawQuestCard(
  ctx: CanvasRenderingContext2D,
  quest: QuestEntry,
  measure: { descLines: string[]; height: number },
  cardX: number,
  cardY: number,
  cardW: number,
  elapsed: number,
): void {
  const { descLines, height } = measure;
  const isActive = quest.status === "active";
  const isCompleted = quest.status === "completed";
  const isLocked = quest.status === "locked";

  // Dimming for completed / locked
  const cardAlpha = isCompleted ? 0.55 : isLocked ? 0.4 : 1.0;
  ctx.globalAlpha = cardAlpha;

  // Card background
  drawRoundRect(ctx, cardX, cardY, cardW, height, CARD_RADIUS, C_CARD_BG, C_CARD_BORDER);

  // Left accent bar
  const accentColor = isActive
    ? C_ACCENT_ACTIVE
    : isCompleted
      ? C_ACCENT_COMPLETE
      : C_ACCENT_LOCKED;

  // Active quests get a glowing accent
  if (isActive) {
    const glowAlpha = 0.3 + Math.sin(elapsed * 2.5) * 0.15;
    ctx.save();
    ctx.shadowColor = C_ACCENT_ACTIVE;
    ctx.shadowBlur = 6;
    ctx.globalAlpha = cardAlpha * glowAlpha;
    ctx.fillStyle = C_ACCENT_ACTIVE;
    drawRoundRect(
      ctx,
      cardX + 2, cardY + 4,
      ACCENT_BAR_W, height - 8,
      ACCENT_BAR_W / 2,
    );
    ctx.fill();
    ctx.restore();
    ctx.globalAlpha = cardAlpha;
  }

  // Solid accent bar
  ctx.fillStyle = accentColor;
  drawRoundRect(
    ctx,
    cardX + 2, cardY + 4,
    ACCENT_BAR_W, height - 8,
    ACCENT_BAR_W / 2,
    accentColor,
  );

  // Content area
  const contentX = cardX + CARD_INNER_PAD + ACCENT_BAR_W + 4;
  const contentW = cardW - CARD_INNER_PAD * 2 - ACCENT_BAR_W - 4;
  let cursorY = cardY + CARD_INNER_PAD;

  // Status icon area (right side of title)
  const iconX = cardX + cardW - CARD_INNER_PAD - 10;
  const iconCY = cursorY + 5;

  if (isCompleted) {
    // Checkmark
    ctx.strokeStyle = C_COMPLETE_CHECK;
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(iconX - 4, iconCY);
    ctx.lineTo(iconX - 1, iconCY + 3);
    ctx.lineTo(iconX + 4, iconCY - 3);
    ctx.stroke();
  } else if (isLocked) {
    // Lock icon — small padlock
    const lx = iconX;
    const ly = iconCY;
    ctx.strokeStyle = C_LOCK_ICON;
    ctx.lineWidth = 1.2;
    ctx.lineCap = "round";
    // Lock body
    drawRoundRect(ctx, lx - 3.5, ly - 1, 7, 6, 1, C_LOCK_ICON);
    // Lock shackle
    ctx.beginPath();
    ctx.arc(lx, ly - 2, 3, Math.PI, 0);
    ctx.stroke();
  }

  // Title
  const titleMaxW = contentW - (isCompleted || isLocked ? 18 : 0);
  drawText(ctx, quest.title, contentX, cursorY, titleMaxW, FONT_TITLE, C_TEXT_TITLE);
  cursorY += LINE_HEIGHT + 2;

  // Description lines
  for (const line of descLines) {
    drawText(ctx, line, contentX, cursorY, contentW, FONT_DESC, C_TEXT_DESC);
    cursorY += LINE_HEIGHT;
  }

  // Progress bar (not for locked quests)
  if (!isLocked) {
    cursorY += 3;
    const barW = contentW - 28;
    drawProgressBar(ctx, contentX, cursorY, barW, PROGRESS_BAR_H, quest.progress, C_PROGRESS_FG, C_PROGRESS_BG);

    // Percentage label
    const pctText = `${Math.round(quest.progress * 100)}%`;
    drawText(ctx, pctText, contentX + barW + 4, cursorY - 1, 28, FONT_SMALL, C_TEXT_DIM);
  }

  ctx.globalAlpha = 1;
}

// ─── Whisper Card Renderer ───────────────────────────────────────────────────

function measureWhisperCard(
  ctx: CanvasRenderingContext2D,
  whisper: WhisperEntry,
  cardW: number,
): { textLines: string[]; height: number } {
  const textW = cardW - CARD_INNER_PAD * 2 - 16; // extra indent for quote mark space
  const textLines = wrapText(ctx, whisper.text, textW, FONT_WHISPER);
  const textH = textLines.length * LINE_HEIGHT;
  const timestampH = LINE_HEIGHT;
  const height = Math.max(
    WHISPER_CARD_MIN_H,
    CARD_INNER_PAD + textH + 4 + timestampH + CARD_INNER_PAD,
  );
  return { textLines, height };
}

function drawWhisperCard(
  ctx: CanvasRenderingContext2D,
  whisper: WhisperEntry,
  measure: { textLines: string[]; height: number },
  cardX: number,
  cardY: number,
  cardW: number,
): void {
  const { textLines, height } = measure;

  // Card background — subtle, almost transparent
  drawRoundRect(ctx, cardX, cardY, cardW, height, CARD_RADIUS, C_CARD_BG, C_CARD_BORDER);

  // Quotation mark watermark — large, faint, top-left
  ctx.globalAlpha = 1;
  ctx.font = FONT_QUOTE_MARK;
  ctx.fillStyle = C_WHISPER_QUOTE;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("\u201C", cardX + CARD_INNER_PAD - 2, cardY + CARD_INNER_PAD - 6);

  // Whisper text — italic, indented past the quote mark
  const textX = cardX + CARD_INNER_PAD + 14;
  let cursorY = cardY + CARD_INNER_PAD;
  ctx.font = FONT_WHISPER;
  ctx.fillStyle = C_WHISPER_TEXT;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  for (const line of textLines) {
    ctx.fillText(line, textX, cursorY);
    cursorY += LINE_HEIGHT;
  }

  // Timestamp — right-aligned, dim
  cursorY += 4;
  drawText(
    ctx,
    whisper.timestamp,
    cardX + cardW - CARD_INNER_PAD, cursorY,
    cardW * 0.5,
    FONT_SMALL, C_WHISPER_TIME, "right",
  );
}

// ─── Tab Bar Renderer ────────────────────────────────────────────────────────

function drawTabBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  activeTab: TabId,
): void {
  // Background
  ctx.fillStyle = C_TAB_BG;
  ctx.fillRect(x, y, w, TAB_BAR_H);

  const tabs: { id: TabId; label: string }[] = [
    { id: "quests", label: "Quests" },
    { id: "whispers", label: "Whispers" },
  ];

  const tabW = w / tabs.length;

  for (let i = 0; i < tabs.length; i++) {
    const tab = tabs[i];
    const tx = x + i * tabW;
    const isActive = tab.id === activeTab;

    // Label
    const color = isActive ? C_TAB_ACTIVE : C_TAB_INACTIVE;
    drawText(
      ctx,
      tab.label,
      tx + tabW / 2, y + 8,
      tabW - 8,
      FONT_TAB, color, "center",
    );

    // Active indicator bar
    if (isActive) {
      const indicW = 28;
      const indicX = tx + tabW / 2 - indicW / 2;
      drawRoundRect(
        ctx,
        indicX, y + TAB_BAR_H - 3,
        indicW, 2,
        1,
        C_TAB_INDICATOR,
      );
    }
  }

  // Bottom separator
  ctx.fillStyle = "rgba(200,168,120,0.08)";
  ctx.fillRect(x, y + TAB_BAR_H - 0.5, w, 0.5);
}

// ─── Scroll Helpers ──────────────────────────────────────────────────────────

function getScrollOffset(appState: JournalAppState): number {
  return appState.activeTab === "quests"
    ? appState.questScrollOffset
    : appState.whisperScrollOffset;
}

function setScrollOffset(appState: JournalAppState, value: number): void {
  if (appState.activeTab === "quests") {
    appState.questScrollOffset = value;
  } else {
    appState.whisperScrollOffset = value;
  }
}

function getScrollVelocity(appState: JournalAppState): number {
  return appState.activeTab === "quests"
    ? appState.questScrollVelocity
    : appState.whisperScrollVelocity;
}

function setScrollVelocity(appState: JournalAppState, value: number): void {
  if (appState.activeTab === "quests") {
    appState.questScrollVelocity = value;
  } else {
    appState.whisperScrollVelocity = value;
  }
}

function getMaxScroll(appState: JournalAppState): number {
  return appState.activeTab === "quests"
    ? appState.questMaxScroll
    : appState.whisperMaxScroll;
}

function setMaxScroll(appState: JournalAppState, value: number): void {
  if (appState.activeTab === "quests") {
    appState.questMaxScroll = value;
  } else {
    appState.whisperMaxScroll = value;
  }
}

function clampScroll(offset: number, totalContentH: number, viewH: number): number {
  const maxScroll = Math.max(0, totalContentH - viewH);
  return Math.max(0, Math.min(offset, maxScroll));
}

// ─── The App ─────────────────────────────────────────────────────────────────

const journalApp: DeviceApp = {
  id: APP_ID,
  name: APP_NAME,
  iconGradient: ["#8b5cf6", "#6366f1"],

  // ── Icon: an open book ──────────────────────────────────────────────────

  drawIcon(ctx, cx, cy, size) {
    const s = size * 0.36;

    ctx.save();
    ctx.strokeStyle = "#ffffff";
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 1.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Left page — curved
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.7);
    ctx.quadraticCurveTo(cx - s * 0.9, cy - s * 0.8, cx - s * 0.95, cy - s * 0.5);
    ctx.lineTo(cx - s * 0.95, cy + s * 0.55);
    ctx.quadraticCurveTo(cx - s * 0.6, cy + s * 0.45, cx, cy + s * 0.7);
    ctx.closePath();
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.fill();
    ctx.stroke();

    // Right page — curved
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.7);
    ctx.quadraticCurveTo(cx + s * 0.9, cy - s * 0.8, cx + s * 0.95, cy - s * 0.5);
    ctx.lineTo(cx + s * 0.95, cy + s * 0.55);
    ctx.quadraticCurveTo(cx + s * 0.6, cy + s * 0.45, cx, cy + s * 0.7);
    ctx.closePath();
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.fill();
    ctx.stroke();

    // Spine line
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.7);
    ctx.lineTo(cx, cy + s * 0.7);
    ctx.strokeStyle = "rgba(255,255,255,0.6)";
    ctx.stroke();

    // Text lines on left page
    ctx.strokeStyle = "rgba(255,255,255,0.3)";
    ctx.lineWidth = 0.6;
    for (let i = 0; i < 3; i++) {
      const ly = cy - s * 0.25 + i * (s * 0.25);
      const indent = i === 0 ? 0.15 : 0;
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.7 + indent * s, ly);
      ctx.lineTo(cx - s * 0.15, ly);
      ctx.stroke();
    }

    // Text lines on right page
    for (let i = 0; i < 3; i++) {
      const ly = cy - s * 0.25 + i * (s * 0.25);
      const shorter = i === 2 ? 0.2 : 0;
      ctx.beginPath();
      ctx.moveTo(cx + s * 0.15, ly);
      ctx.lineTo(cx + s * 0.7 - shorter * s, ly);
      ctx.stroke();
    }

    ctx.restore();
  },

  // ── Content ──────────────────────────────────────────────────────────────

  drawContent(ctx, rect, state, dt) {
    const appState = getAppState(state);
    appState.elapsed += dt;

    const { x, y, w, h } = rect;

    // ── Tab bar ──────────────────────────────────────────────────────────
    drawTabBar(ctx, x, y, w, appState.activeTab);

    // ── Content area ─────────────────────────────────────────────────────
    const contentY = y + TAB_BAR_H;
    const contentH = h - TAB_BAR_H;
    const cardW = w - CARD_PAD_X * 2;

    // Clip to content area
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, contentY, w, contentH);
    ctx.clip();

    // ── Momentum / inertia scroll ─────────────────────────────────────
    const velocity = getScrollVelocity(appState);
    if (Math.abs(velocity) > 0.5) {
      setScrollOffset(appState, getScrollOffset(appState) + velocity * dt);
      setScrollVelocity(appState, velocity * Math.exp(-6 * dt));
    } else {
      setScrollVelocity(appState, 0);
    }

    const scrollOffset = getScrollOffset(appState);

    if (appState.activeTab === "quests") {
      // ── Quests Tab ─────────────────────────────────────────────────
      // Read from player state, sort: active first, then completed, then locked
      const sortOrder: Record<string, number> = { active: 0, completed: 1, locked: 2 };
      const sorted = getQuestEntries(state).sort(
        (a, b) => (sortOrder[a.status] ?? 3) - (sortOrder[b.status] ?? 3),
      );

      // Measure all cards
      const measures = sorted.map(q => measureQuestCard(ctx, q, cardW));
      const totalH = measures.reduce((sum, m) => sum + m.height + CARD_GAP, 0) - CARD_GAP + CARD_GAP * 2;

      // Clamp scroll and store max
      appState.questMaxScroll = Math.max(0, totalH - contentH);
      const clampedScroll = clampScroll(scrollOffset, totalH, contentH);
      appState.questScrollOffset = clampedScroll;

      let drawY = contentY + CARD_GAP - clampedScroll;

      for (let i = 0; i < sorted.length; i++) {
        const quest = sorted[i];
        const measure = measures[i];

        // Skip cards outside viewport
        if (drawY + measure.height < contentY) {
          drawY += measure.height + CARD_GAP;
          continue;
        }
        if (drawY > contentY + contentH) break;

        drawQuestCard(
          ctx, quest, measure,
          x + CARD_PAD_X, drawY, cardW,
          appState.elapsed,
        );

        drawY += measure.height + CARD_GAP;
      }

      // Empty state
      if (sorted.length === 0) {
        ctx.globalAlpha = 0.35;
        drawText(
          ctx, "No quests yet.",
          x + w / 2, contentY + contentH / 2 - 6,
          w - 32, "italic 10px sans-serif", C_TEXT_DIM, "center",
        );
        drawText(
          ctx, "The Hearth will find you when it's ready.",
          x + w / 2, contentY + contentH / 2 + 8,
          w - 32, "italic 9px sans-serif", C_TEXT_DIM, "center",
        );
        ctx.globalAlpha = 1;
      }
    } else {
      // ── Whispers Tab ───────────────────────────────────────────────
      const whispers = getWhisperEntries(state);
      const measures = whispers.map(wh => measureWhisperCard(ctx, wh, cardW));
      const totalH = measures.reduce((sum, m) => sum + m.height + CARD_GAP, 0) - CARD_GAP + CARD_GAP * 2;

      appState.whisperMaxScroll = Math.max(0, totalH - contentH);
      const clampedScroll = clampScroll(scrollOffset, totalH, contentH);
      appState.whisperScrollOffset = clampedScroll;

      let drawY = contentY + CARD_GAP - clampedScroll;

      for (let i = 0; i < whispers.length; i++) {
        const whisper = whispers[i];
        const measure = measures[i];

        if (drawY + measure.height < contentY) {
          drawY += measure.height + CARD_GAP;
          continue;
        }
        if (drawY > contentY + contentH) break;

        drawWhisperCard(ctx, whisper, measure, x + CARD_PAD_X, drawY, cardW);
        drawY += measure.height + CARD_GAP;
      }

      // Empty state
      if (whispers.length === 0) {
        ctx.globalAlpha = 0.35;
        drawText(
          ctx, "Silence, for now.",
          x + w / 2, contentY + contentH / 2 - 6,
          w - 32, "italic 10px sans-serif", C_TEXT_DIM, "center",
        );
        drawText(
          ctx, "The Aeterna's words will gather here.",
          x + w / 2, contentY + contentH / 2 + 8,
          w - 32, "italic 9px sans-serif", C_TEXT_DIM, "center",
        );
        ctx.globalAlpha = 1;
      }
    }

    // Top fade — content scrolls under
    const fadeH = 12;
    const fadeGrad = ctx.createLinearGradient(x, contentY, x, contentY + fadeH);
    fadeGrad.addColorStop(0, "rgba(14,12,10,1)");
    fadeGrad.addColorStop(1, "rgba(14,12,10,0)");
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(x, contentY, w, fadeH);

    // Bottom fade
    const btmFadeGrad = ctx.createLinearGradient(x, contentY + contentH - fadeH, x, contentY + contentH);
    btmFadeGrad.addColorStop(0, "rgba(14,12,10,0)");
    btmFadeGrad.addColorStop(1, "rgba(14,12,10,1)");
    ctx.fillStyle = btmFadeGrad;
    ctx.fillRect(x, contentY + contentH - fadeH, w, fadeH);

    ctx.restore(); // clip
  },

  // ── Tap ──────────────────────────────────────────────────────────────────

  handleTap(localX, localY, state) {
    const appState = getAppState(state);

    // Tab bar taps
    if (localY < TAB_BAR_H) {
      // Split at midpoint of actual content width
      const contentRect = state.player.device.appContentRect;
      const halfW = contentRect ? contentRect.w / 2 : 86;
      if (localX < halfW) {
        if (appState.activeTab !== "quests") {
          appState.activeTab = "quests";
          return true;
        }
      } else {
        if (appState.activeTab !== "whispers") {
          appState.activeTab = "whispers";
          return true;
        }
      }
      return false;
    }

    return false;
  },

  // ── Drag scroll ──────────────────────────────────────────────────────

  handleDrag(dy, state) {
    const appState = getAppState(state);
    const maxScroll = getMaxScroll(appState);
    setScrollOffset(appState, Math.max(0, Math.min(getScrollOffset(appState) + dy, maxScroll)));
    setScrollVelocity(appState, 0);
  },

  handleDragEnd(velocityY, state) {
    const appState = getAppState(state);
    setScrollVelocity(appState, velocityY);
  },

  // ── Lifecycle ──────────────────────────────────────────────────────────

  onOpen(state) {
    // Ensure state exists
    const appState = getAppState(state);
    appState.questScrollVelocity = 0;
    appState.whisperScrollVelocity = 0;
  },

  // ── Badge: show count of active quests ─────────────────────────────────

  badge(state) {
    const activeCount = state.player.quests.filter(q => q.status === "active" || q.status === "available").length;
    return activeCount;
  },
};

// ─── Self-Register ───────────────────────────────────────────────────────────

registerApp(journalApp);
